import { call, put, select, takeEvery } from 'redux-saga/effects'
import { addAnswer } from '../answers-slice'
import { Answers, Scenario, ScenarioType, Steps, UNLIMITED_DATA } from '../interfaces'
import { nextStep } from '../step-slice'
import { selectAnswers, selectScenario, selectScenarioAdditionalInformation } from '../selectors'
import { calculateInternetUsageInKbps, InternetUsageResponse } from '../../internet'
import { finishScenario } from '../scenario-slice'
import { getCookie } from '../../scenarios'

export function* internetSaga() {
  yield takeEvery(nextStep.type, newStep)
  yield takeEvery(addAnswer.type, answerAdded)
}

export function* newStep({ payload }: ReturnType<typeof nextStep>) {
  if (payload?.id === Steps.INTERNET_USAGE || payload?.id === Steps.INTERNET_USAGE_INFO_ONLY) {
    yield call(calculateInternetUsageAnswers)
  }

  const answers: Answers = yield select(selectAnswers)

  if (payload?.id === Steps.ADDRESS_LOCATION) {
    if (getCookie('internetSearchPostalcode')) {
      if (answers && answers['internet-usage']) {
        yield put(
          addAnswer({
            stepId: Steps.ADDRESS_LOCATION,
            answerIds: ['No address', 'finishScenario'] as string[],
            hidden: false,
          }),
        )
      }
    }
  }
}

function* calculateInternetUsageAnswers() {
  const answers: Answers = yield select(selectAnswers)
  const scenario: Scenario = yield select(selectScenario)
  let downloadSpeed: string | number = 0
  let monthlyUsageInKbps: number | UNLIMITED_DATA = 0

  if (!(answers['internet-speed'] && answers['internet-speed']![0])) {
    const internetUsage: InternetUsageResponse = yield call(calculateInternetUsageInKbps, {
      answers,
    })
    downloadSpeed = internetUsage.downloadSpeed
    monthlyUsageInKbps = internetUsage.monthlyUsageInKbps
  } else {
    if (answers['slider-data-amount'] && answers['slider-data-amount']![0]) {
      downloadSpeed = answers['slider-data-amount']![0]
    } else {
      downloadSpeed = answers['internet-speed']![0]
    }
    monthlyUsageInKbps = Number(downloadSpeed) === 0 ? 0 : Number(downloadSpeed) >= 60 ? 'unlimited-data' : 100000
  }

  if (scenario.type === ScenarioType.ADVISE_INTERNET) {
    downloadSpeed = yield findClosestAvailableDownloadSpeedForCurrentProvider(Number(downloadSpeed))
  }

  if (!answers['download-speed']) {
    yield put(
      addAnswer({ stepId: Steps.DOWNLOAD_SPEED, answerIds: [downloadSpeed.toString()] as string[], hidden: true }),
    )
  }

  if (!answers['monthly-usage']) {
    yield put(
      addAnswer({ stepId: Steps.MONTHLY_USAGE, answerIds: [monthlyUsageInKbps.toString()] as string[], hidden: true }),
    )
  }
}

export function* answerAdded(action: ReturnType<typeof addAnswer>) {
  // Maybe next time some other component uses this approach inter-exchange lines 48 and 49
  if (action.payload.stepId === Steps.POSTAL_CODE) {
    if (action.payload.answerIds.includes('finishScenario')) {
      yield put(finishScenario({ [action.payload.stepId]: action.payload.answerIds }))
    }
  }
}

export function* findClosestAvailableDownloadSpeedForCurrentProvider(downloadSpeed: number) {
  const scenarioAdditionalInformation: Record<string, unknown> = yield select(selectScenarioAdditionalInformation)
  if (scenarioAdditionalInformation?.downloadSpeeds) {
    const speeds = scenarioAdditionalInformation?.downloadSpeeds as Array<number>
    const maxSpeed = Math.max(...speeds) // Get the highest value in the array
    return speeds.reduce((previousValue: number, currentValue: number): number => {
      if (currentValue <= downloadSpeed) {
        return previousValue
      }
      return Math.abs(currentValue - downloadSpeed) < Math.abs(previousValue - downloadSpeed)
        ? currentValue
        : previousValue
    }, maxSpeed)
  }
}
